import { Component, inject, OnInit, signal } from '@angular/core';
import { NavController, Platform, ToastController } from '@ionic/angular';
import { of, Subject } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AuthService } from 'src/app/services/auth/auth.service';
import { LoginService, PostAuthDestination } from 'src/app/services/login/login.service';
import { NetworkService } from 'src/app/services/network/network.service';
import { PlatformService } from 'src/app/services/platform/platform.service';
import { StorageService } from 'src/app/services/storage/storage.service';
import { ActivatedRoute } from '@angular/router';
import { Logger, LoggingService } from 'ionic-logging-service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { TenantService } from '@app/services/tenant/tenant.service';
import { PageTitleService } from '@app/services/page-title/page-title.service';
import { ErrorService } from '@app/services/error/error.service';
import { LogoutService } from '@app/services/logout/logout.service';
import { PkceService } from '@app/services/pkce/pkce.service';
import { CLIENT_ID_BROWSER, CLIENT_ID_NATIVE, SIGN_IN_QUERY_PARAMS } from '@app/services/auth/auth.constants';
import { ShareExtensionService } from '@app/services/share-extension/share-extension.service';

@Component({
    selector: 'app-network-check',
    templateUrl: './network-check.page.html',
    styleUrls: ['./network-check.page.scss'],
    standalone: false
})
export class NetworkCheckPage implements OnInit {
  private route = inject(ActivatedRoute);

  public unSubscribe = new Subject<void>();
  public isCordova: boolean = this.platformService.isCordova();
  public isOnline = this.networkService.hasInternetAccess;
  public isLoading = signal(false);
  public errorMessage: string = null;
  public showUnsupportedVersionMessage: boolean = false;
  public APP_ID: string = 'id1391512097';
  public APP_PACKAGE_NAME: string = 'net.printix.printixapp';
  public EULA_ACCEPTED_STORAGE_STRING = 'eula-accepted';
  public EULA_ACCEPTED_STORAGE_KEY = 'is-eula-accepted';
  public showEulaText = false;
  private registrationCode: string = null;
  private refreshTokenParam: string = null;
  private queryParams: { [key: string]: string | undefined };
  private signOutParam: string | null = null;
  private logger: Logger;
  private WEB_APP_SCHEME = 'https://';
  private urlTenantDomain: string;
  public  signInCode: string | null = null;
  public eulaHtml: string;

  constructor (
    private authService: AuthService,
    private environmentService: EnvironmentService,
    private errorService: ErrorService,
    private loginService: LoginService,
    private logoutService: LogoutService,
    private loggingService: LoggingService,
    public navController: NavController,
    private networkService: NetworkService,
    private pageTitleService: PageTitleService,
    public platform: Platform,
    private pkceService: PkceService,
    private platformService: PlatformService,
    private storageService: StorageService,
    private tenantService: TenantService,
    private shareExtensionService: ShareExtensionService,
    private toastController: ToastController,
    private httpClient: HttpClient
  ) {
    this.logger = this.loggingService.getLogger("[NetworkCheckPage]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  ngOnInit() {
    this.pageTitleService.setTitle('');
    this.platform.ready().then(() => {
      this.logger.info('ngOnInit() - Platform is ready');
      if (this.isCordova) {
        this.storageService.getDatabaseState().pipe(
          filter(isReady => isReady),
          take(1)
          // check localstorage for 'EULA accepted'
        ).subscribe(() => this.checkForAcceptedEula());
      } else {
        this.setUrlTenantdomain();
        this.setQueryParams();
      }
    });
  }

  private setUrlTenantdomain() {
    this.logger.info('setUrlTenantdomain()');
    this.urlTenantDomain = location.hostname;
    this.tenantService.setTenantInputValues(this.urlTenantDomain, null);
  }

  private checkForAcceptedEula(): void {
    this.logger.info('checkForAcceptedEula()');
    this.storageService.getItemFromLocalStorage(this.EULA_ACCEPTED_STORAGE_KEY, 'checkForAcceptedEula()')
    .pipe(takeUntil(this.unSubscribe))
    .subscribe((EULA_ACCEPTED) => {
      if (EULA_ACCEPTED === this.EULA_ACCEPTED_STORAGE_STRING) {
        this.setQueryParams();
      } else {
        this.showEulaText = true;
        this.loadEulaText();
      }
      this.unSubscribe.next();
      this.unSubscribe.complete();
    });
  }

  loadEulaText() {
    this.httpClient.get('assets/eula-text/eula.html', { responseType: 'text' }).subscribe(html => {
      this.eulaHtml = html;
    });
  }

  public eulaAccepted() {
    this.storageService.addItemToLocalStorage(this.EULA_ACCEPTED_STORAGE_KEY, this.EULA_ACCEPTED_STORAGE_STRING)
    .pipe(takeUntil(this.unSubscribe))
    .subscribe(() => {
      this.showEulaText = false;
      this.setQueryParams();
    })
  }

  public eulaDenied() {
    this.showEulaText = false;
    navigator['app'].exitApp();
  }

  private setQueryParams(): void {
    this.logger.info('setQueryParams()');
    this.route.queryParams.pipe(take(1))
    .subscribe(params => {
      this.queryParams = params;
      this.signInCode = this.queryParams["code"] ? this.queryParams["code"] : null;
      this.registrationCode = this.queryParams['registrationCode'] ? this.queryParams['registrationCode'] : null;
      this.refreshTokenParam = this.getQueryParamFromHash('refreshToken');
      this.signOutParam = params['signOutParam'] ? params['signOutParam'] : null;
      if (this.signOutParam === 'USER_SIGN_OUT') {
        this.logger.info('signOutParam USER_SIGN_OUT');
        this.endSsoSession();
      } else if (this.signOutParam) {
        this.logger.info('signOutParam' + this.signOutParam);
        this.handleUnverifiedCredentials();
      } else {
        this.setEnvironmentPrefix();
      }
    });
  }

  private getQueryParamFromHash(param: string): string | null {
    const hash = window.location.hash;
    const queryString = hash.includes('?') ? hash.split('?')[1] : '';
    const params = new URLSearchParams(queryString);
    return params.get(param);
  }

  private setEnvironmentPrefix(): void {
    if (this.isCordova) {
      this.environmentService.setEnvironmentPrefixFromStorage().subscribe((environmentPrefix) => {
        // this.checkAppVersionState();
        // outcommented call to checkAppVersionState(), since it has not been used for years
        // and it needs a reworking to accomodate multiple datacenters, both for the endpoint and
        // also not to be checked so early in the app, before the currentEnvironmentEndpoints are set.
        this.verifyUserCredentials();
      });
    } else {
      if (!this.urlTenantDomain.includes('localhost')) {
        this.environmentService.setEnvironmentVariablesFromUrl(this.urlTenantDomain).subscribe((response) => {
          // this.checkAppVersionState();
          // outcommented call to checkAppVersionState(), since it has not been used for years
          // and it needs a reworking to accomodate multiple datacenters, both for the endpoint and
          // also not to be checked so early in the app, before the currentEnvironmentEndpoints are set.
          this.verifyUserCredentials();
        });
      } else {
        this.verifyUserCredentials();
      }
    }
  }

 private verifyUserCredentials(): void {
    const methodName = 'verifyUserCredentials() '
    this.logger.info(methodName);
    this.isLoading.set(true);
    if (this.refreshTokenParam) {
      this.logger.info(methodName + 'url includes "refreshToken="');
      this.logInFromTenantHub();
    } else {
      this.logger.info(methodName + 'url does NOT include "refreshToken="');
      this.authService.verifyUserCredentials()
      .pipe(
        takeUntil(this.unSubscribe),
        // Returning user — finding a stored refresh token means any
        // idpLoginToken in storage is leftover from a previous (incomplete)
        // activation chain. Clear it before resolvePostAuthNavigation$ runs so
        // the tenant-list call is not routed through internal-auth validation.
        switchMap(() => this.loginService.clearIdpLoginToken$())
      )
      .subscribe(() => {
        this.unSubscribe.next();
        this.unSubscribe.complete();
        this.authSuccessful();
      }, () => {
        this.unSubscribe.next();
        this.unSubscribe.complete();
        this.isLoading.set(false);
        if (this.signInCode) {
          this.handleUserAuthentication();
        } else {
          this.handleUnverifiedCredentials();
        }
      });
    }
  }

  // Final step of the sign-out flow on web: POST ENDPOINT_SIGN_OUT_BROWSER (via
  // LogoutService) so the auth server clears the SSO session cookie (200 OK, no
  // redirect), then route to the sign-in app ourselves. The call is tolerant of
  // failure — we continue either way. On localhost dev there is no real auth
  // server, so we just fall back to the sign-in page.
  private endSsoSession(): void {
    const methodName = 'endSsoSession() ';
    this.logger.info(methodName);
    if (this.urlTenantDomain && this.urlTenantDomain.includes('localhost')) {
      this.logger.info(methodName + 'localhost — skipping browser sign-out, go to sign-in page');
      this.navController.navigateRoot(['/sign-in']);
      return;
    }
    this.logoutService.endBrowserSsoSession().pipe(take(1))
    .subscribe(() => {
      this.logger.info(methodName + 'browser SSO session ended — continue to sign-in app');
      this.handleUnverifiedCredentials();
    });
  }

  private handleUnverifiedCredentials() {
    const methodName = 'handleUnverifiedCredentials() '
    this.logger.info(methodName);
    this.logger.info(methodName + 'url does not contain "code="');
    if (!this.isCordova && !this.urlTenantDomain.includes('localhost')) { // Webapp, not in development
      if (this.signOutParam === 'ACCESS_DENIED') {
        this.showErrorToast('ACCESS_DENIED').then(() => {
          this.goToBrowserSignIn();
        });
      } else {
        this.goToBrowserSignIn();
      }
    }  else {
      this.logger.info(methodName + 'Go to login page');
      this.navController.navigateRoot(['/sign-in']);
    }
  }

  // Browser platform: hand off to the sign-in app (separate web app) and let it
  // own the IDP session. Unlike native — where login.page.ts catches the
  // idpLoginToken off the printixapp:/ deeplink and stores it via
  // TenantService.setIdpLoginToken() — on web this app never sees the idp token;
  // the sign-in app handles it and returns only the PKCE `code` we exchange here.
  private async goToBrowserSignIn(): Promise<void> {
    this.logger.info('goToBrowserSignIn()');
    const signInApp_Url = await this.buildSignInUrl();
    this.logger.info('goToBrowserSignIn() - signInAppUrl: ' + signInApp_Url);
    window.open(signInApp_Url.toString(), '_self');
    // Testing registrationCode functionality in regard to Printix GO and on-device
    // append /?registrationCode=385480 or /?registrationCode=FORGOT_CODE after Tenant domain in the url
  }

  private async buildSignInUrl(): Promise<URL> {
    this.logger.info('buildSignInUrl()');
    const ENDPOINT_TENANT_DOMAIN = this.environmentService.getEnvironmentEndpoint['ENDPOINT_TENANT_DOMAIN'];
    const signInApp_Url = new URL(`${ENDPOINT_TENANT_DOMAIN}${this.urlTenantDomain}`);
    const redirectUrl = new URL(`${this.WEB_APP_SCHEME}${this.urlTenantDomain}`);

    if (this.registrationCode) {
      redirectUrl.searchParams.set('registrationCode', this.registrationCode);
    }

    SIGN_IN_QUERY_PARAMS.forEach(param => {
      const key = Object.keys(param)[0];
      let value = param[key];
      if (key === 'client_id') {
          value = this.isCordova ? CLIENT_ID_NATIVE : CLIENT_ID_BROWSER;
      }
      if (key === 'redirect_uri') {
          value = redirectUrl.toString();
      }
      signInApp_Url.searchParams.set(key, value);
    });

    // Add PKCE parameters for enhanced security sign-in flow
    const codeChallenge = await this.pkceService.generatePkceChallenge();
    signInApp_Url.searchParams.set('code_challenge', codeChallenge);
    signInApp_Url.searchParams.set('code_challenge_method', 'S256');
    return signInApp_Url;
  }

  private handleUserAuthentication(): void {
    this.logger.info('handleUserAuthentication()');
    this.isLoading.set(true);
    // Pass registrationCode so the token-request redirect_uri matches the one the
    // authorization request used (RFC 6749 §4.1.3) on the GO-registration web flow.
    this.authService.exchangeCodeForTokens(this.signInCode, this.urlTenantDomain, this.registrationCode).pipe(take(1))
    .subscribe({
      next: () => {
        this.logger.info('handleUserAuthentication() - verified');
        this.authSuccessful();
      },
      error: (error) => {
        this.isLoading.set(false);
        this.logger.error('handleUserAuthentication() ERROR: ' + JSON.stringify(error));
        // Non-HTTP errors (e.g. PKCE code_verifier missing) are precondition failures,
        // not server errors — route back to /sign-in so the user can restart the flow
        // instead of seeing a generic HTTP error modal that doesn't fit the situation.
        if (!(error instanceof HttpErrorResponse)) {
          this.signInCode = null;
          if (!this.isCordova && !this.urlTenantDomain.includes('localhost')) {
            this.goToBrowserSignIn();
          } else {
            this.navController.navigateRoot(['/sign-in']);
          }
          return;
        }
        this.errorService.handleHttpClientResponseError(error, 'GET', 'handleUserAuthentication')
        .subscribe(result => {
          if (result === 'unauthenticated') {
            this.logger.info('User unauthenticated');
          }
        });
      }
    });
  }

  public authSuccessful() {
    this.logger.info('authSuccessful()');
    this.isLoading.set(true);
    let queryParams: { [key: string]: string } = {};
    this.route.queryParams.pipe(
      take(1),
      switchMap(params => {
        queryParams = params['registrationCode'] ? { registrationCode: params['registrationCode']} : {};
        return this.loginService.resolvePostAuthNavigation$().pipe(
          tap((destination: PostAuthDestination) => this.navigatePostAuth(destination, queryParams))
        );
      }),
      catchError(error => {
        this.isLoading.set(false);
        this.logger.error('authSuccessful() - Error during post-auth navigation - status: '
          + (error?.status || 'unknown') + ', statusText: ' + (error?.statusText || ''));
        this.navController.navigateRoot(['/tenant-select'], { queryParams });
        return of(null);
      })
    ).subscribe();
  }

  private navigatePostAuth(destination: PostAuthDestination, queryParams: { [key: string]: string }): void {
    if (destination === 'app') {
      const registrationCode = queryParams['registrationCode'];
      if (registrationCode) {
        this.navController.navigateRoot(['/go'], { queryParams: { registrationCode } });
        return;
      }
      if (this.shareExtensionService.pendingShareIntent()) {
        this.navController.navigateRoot(['/document-print']);
        return;
      }
      this.navController.navigateRoot(['/print-jobs']);
    } else {
      this.navController.navigateRoot(['/tenant-select'], { queryParams });
    }
  }

  private logInFromTenantHub() {
    this.logger.info('logInFromTenantHub()');
    let refreshToken = location.hash.split('=')[1];
    this.authService.performTokenRefresh(refreshToken)
    .pipe(
      // Returning user via tenant-hub refresh — any idpLoginToken in storage
      // is leftover from a previous activation chain. Clear it before
      // authSuccessful() so the tenant-list call is not routed through
      // internal-auth validation.
      switchMap(() => this.loginService.clearIdpLoginToken$())
    )
    .subscribe({
      next: () => {
        this.authSuccessful();
      },
      error: (error) => {
        this.isLoading.set(false);
        this.logger.error('logInFromTenantHub() - performTokenRefresh failed: ' + JSON.stringify(error));
        this.handleUnverifiedCredentials();
      }
    });
  }

  private async showErrorToast(message: string): Promise<void> {
    const toast = await this.toastController.create({
      message: message,
      color: 'danger',
      duration: 5000,
      position: 'top'
    });
    await toast.present();
  }
}
